import type { UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { documentsQueryKey } from '../../fetchers/documents/api/use-invalidate-documents';
import type { DocumentsResponse, UploadUrlsResponse } from '../../fetchers/documents/documents-fetchers';
import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';

type UseDocumentsOptions = {
    readonly searchTerm?: string;
};

export const useDocuments = (options: UseDocumentsOptions = {}): UseQueryResult<DocumentsResponse, Error> => {
    const { searchTerm } = options;
    const { fetchDocuments } = useApplicationFetchers();

    return useQuery({
        queryFn: async () => {
            const result = await fetchDocuments(searchTerm);

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },

        queryKey: searchTerm !== undefined ? [...documentsQueryKey, searchTerm] : documentsQueryKey,
        throwOnError: false,
    });
};

export const useDeleteDocument = (): {
    readonly deleteDocument: (documentId: string) => Promise<void>;
    readonly deleteDocuments: (documentIds: readonly string[]) => Promise<void>;
    readonly isDeleting: boolean;
} => {
    const { deleteDocument, deleteDocuments } = useApplicationFetchers();
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: async (documentId: string) => {
            const result = await deleteDocument(documentId);

            if (result.isErr) {
                throw result.error;
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: documentsQueryKey });
        },
    });

    const batchMutation = useMutation({
        mutationFn: async (documentIds: readonly string[]) => {
            const result = await deleteDocuments(documentIds);

            if (result.isErr) {
                throw result.error;
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: documentsQueryKey });
        },
    });

    return {
        deleteDocument: mutation.mutateAsync,
        deleteDocuments: batchMutation.mutateAsync,
        isDeleting: mutation.isPending || batchMutation.isPending,
    };
};

export const useDownloadDocument = (): {
    readonly downloadDocument: (documentId: string) => Promise<void>;
    readonly downloadDocuments: (documentIds: readonly string[]) => Promise<void>;
} => {
    const { downloadDocumentsAsZip, fetchDownloadUrl } = useApplicationFetchers();

    const mutation = useMutation({
        mutationFn: async (documentId: string) => {
            const result = await fetchDownloadUrl(documentId);

            if (result.isErr) {
                throw result.error;
            }

            return result.value;
        },
        onSuccess: (data) => {
            window.open(data.url, '_blank');
        },
    });

    const downloadDocuments = async (documentIds: readonly string[]): Promise<void> => {
        const result = await downloadDocumentsAsZip(documentIds);

        if (result.isErr) {
            throw result.error;
        }
    };

    return {
        downloadDocument: async (documentId: string) => {
            await mutation.mutateAsync(documentId);
        },
        downloadDocuments,
    };
};

export const useGetUploadUrls = (): {
    readonly getUploadUrls: (fileNames: readonly string[]) => Promise<UploadUrlsResponse>;
    readonly isLoading: boolean;
} => {
    const { getUploadUrls } = useApplicationFetchers();

    const mutation = useMutation({
        mutationFn: async (fileNames: readonly string[]) => {
            const result = await getUploadUrls(fileNames);

            if (result.isErr) {
                throw result.error;
            }

            return result.value;
        },
    });

    return {
        getUploadUrls: mutation.mutateAsync,
        isLoading: mutation.isPending,
    };
};
